/**
 * L4: FAKE one-time codes for fixture data. This is NOT RFC 6238 TOTP. J-3704 carries no crypto,
 * so this is a deterministic stand-in (FNV-1a over seed and time window) with the real screen
 * behaviour: digits, period, countdown, red in the last 5 seconds, grouped for reading. J-3705
 * swaps the generator; the view does not change. The seed itself is never returned.
 */
import type { ItemField } from "../contract.js";

export interface OtpView {
  code: string;
  grouped: string;
  secondsRemaining: number;
  period: number;
  /** Last 5 seconds of the window: the Mac panel turns the code red. */
  expiring: boolean;
}

export const EXPIRING_SECONDS = 5;

export function counterAt(now: number, period: number): number {
  return Math.floor(now / 1000 / Math.max(period, 1));
}

export function secondsRemaining(now: number, period: number): number {
  const p = Math.max(period, 1);
  return p - (Math.floor(now / 1000) % p);
}

export function fakeOtp(seed: string, counter: number, digits: number): string {
  let hash = 0x811c9dc5;
  const input = `${seed}:${counter}`;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return String(hash % 10 ** digits).padStart(digits, "0");
}

/** `123456` → `123 456`, `12345678` → `1234 5678` (Mac TOTPClock.grouped). */
export function grouped(code: string): string {
  if (code.length < 6 || code.length % 2 !== 0) return code;
  return `${code.slice(0, code.length / 2)} ${code.slice(code.length / 2)}`;
}

export function otpNow(field: ItemField, now: number): OtpView {
  const period = field.otp?.period ?? 30;
  const digits = field.otp?.digits ?? 6;
  const code = fakeOtp(field.value, counterAt(now, period), digits);
  const left = secondsRemaining(now, period);
  return { code, grouped: grouped(code), secondsRemaining: left, period, expiring: left <= EXPIRING_SECONDS };
}
